import React, { ComponentType, useEffect, useState } from 'react';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';

interface WithCellEventListenerProps {
  cell: ICell;
  className?: string;
  [key: string]: any;
}

interface EnhancedComponentProps extends WithCellEventListenerProps {
  value?: any;
  backgroundColor?: string;
  liveStyle?: React.CSSProperties;
}

// Generated grid cells can retain methods while holding an older value than the workbook.
const resolveLiveCell = (cell: ICell): ICell => {
  const key = cell.totalKey ?? cell.key;
  return (key ? (cell.sheet?.cells?.[key] as ICell | undefined) : undefined) ?? cell;
};

// Empty live style prevents a copy from retaining a highlight the workbook already cleared.
const NO_STYLE: React.CSSProperties = {};

// eslint-disable-next-line react/display-name
const withCellEventListener = <P extends WithCellEventListenerProps>(
  WrappedComponent: ComponentType<P & EnhancedComponentProps>
) =>
    function WithCellEventListener(props: P) {
      const { cell, className } = props;
      const isCopy = typeof cell.getFullKey !== 'function';
      const liveCell = resolveLiveCell(cell);
      const fullKey = liveCell.getFullKey ? liveCell.getFullKey() : undefined;
      const [value, setValue] = useState<any>(liveCell.value);
      const [cellClassName, setCellClassName] = useState<string | undefined>(className);
      const [cellBackgroundColor, setCellBackgroundColor] = useState<string | undefined>();
      // Copies read live style at render so a cleared highlight does not stick.
      const liveStyle = isCopy ? ((liveCell.style ?? NO_STYLE) as React.CSSProperties) : undefined;

      useEffect(() => {
        setCellClassName(className);
      }, [className]);

      // Subscribe to the workbook source, not the generated grid copy, and release old subscriptions.
      useEffect(() => {
        const getValueHandler = () => {
          setValue(liveCell.value);
          const tmpClassName = className ?? '';
          const tmpCellClassName = liveCell.className ?? '';
          const cellClassesSet = new Set([...tmpCellClassName.split(' '), ...tmpClassName.split(' ')]);
          setCellClassName(Array.from(cellClassesSet).join(' '));
          setCellBackgroundColor(liveCell.backgroundColor);
        };

        if (fullKey) {
          document.addEventListener(fullKey, getValueHandler);
          // Read after subscribing, including updates that arrived before this cell mounted.
          setValue(liveCell.value);
          return () => document.removeEventListener(fullKey, getValueHandler);
        }
        setValue(liveCell.value);
        return undefined;
      }, [fullKey, liveCell, className]);

      const enhancedProps = {
        ...props,
        value,
        className: cellClassName,
        backgroundColor: cellBackgroundColor,
        liveStyle,
      } as P & EnhancedComponentProps;

      return React.createElement(WrappedComponent, enhancedProps);
    };

export default withCellEventListener;
